/**
 * throttle.ts — sporočilo ob zadeti omejitvi zahtev (T1886, T1902)
 * =================================================================
 *
 * DRF ob zadeti meri vrne `Throttled`, katerega besedilo je **pol slovensko,
 * pol angleško** (»Zahtevek je bil pridržan. Expected available in 2968
 * seconds.«): prvi stavek je `default_detail` iz slovenskega kataloga, drugi
 * `extra_detail_plural`, ki ga ta katalog nima. Čas je poleg tega v
 * **sekundah** — 2968 s je 49 minut, česar uporabnik ne prebere.
 *
 * Napaka je zato dvojna in je s samim prevodom niza ni mogoče odpraviti:
 * število nosi množino, ki je v slovenščini štiričlena in v hrvaščini
 * tričlena. Številko izriše odjemalec prek i18next, strežniško besedilo pa se
 * zavrže.
 *
 * ⚠️ **Loči se po `code`, ne po golem statusu 429.** Nekatere poti imajo svoj
 * lastni 429 s pravilnim besedilom — podpisna `OTP_COOLDOWN` (»Nova koda je
 * bila poslana pred kratkim.«) — in vzorec »status === 429 → naše besedilo«
 * bi ga tiho požrl.
 *
 * Minute se zaokrožijo **navzgor**: poskus pred iztekom ne reši ničesar, zato
 * je prezgodnja napoved slabša od pol minute predolge.
 */
import { asApiError } from './apiError';

/** Koda, s katero se omejevalnik predstavi (`custom_exception_handler`). */
export const THROTTLED = 'THROTTLED';

/** Ključa prevoda: sporočilo s števcem minut in različica brez znane dobe. */
export interface ThrottleKeys {
  message: string;
  unknown: string;
}

/**
 * Privzeta ključa. Uporabijo ju poti, ki svojega besedila nimajo — prijava,
 * ponastavitev gesla, ponovno pošiljanje pošte, ponovno odprtje računa.
 */
export const COMMON_THROTTLE_KEYS: ThrottleKeys = {
  message: 'common.error_throttled',
  unknown: 'common.error_throttled_unknown',
};

/**
 * Sporočilo o zadeti omejitvi, ali `null`, če napaka ni omejitev — takrat naj
 * klicatelj napako obravnava po svoje.
 *
 * `retry_after` (sekunde) doda zaledje ob `Throttled` (T1886 (a)); glava
 * `Retry-After` za to ne zadošča, ker je ni v `CORS_EXPOSE_HEADERS`.
 */
export function throttleMessage(
  err: unknown,
  t: (key: string, opts?: Record<string, unknown>) => string,
  keys: ThrottleKeys = COMMON_THROTTLE_KEYS,
): string | null {
  const odgovor = asApiError(err).response;
  if (odgovor?.status !== 429 || odgovor?.data?.code !== THROTTLED) return null;
  const sekunde = Number(odgovor.data.retry_after);
  if (!Number.isFinite(sekunde) || sekunde <= 0) return t(keys.unknown);
  return t(keys.message, { count: Math.ceil(sekunde / 60) });
}
